import { createFileRoute, Link } from "@tanstack/react-router";
import { canonical, ROOFING_CONTRACTOR_ID } from "@/components/site/site-config";
import { PageHero, ServicePageBody } from "@/components/site/PageShell";
import { Breadcrumbs, breadcrumbListJsonLd } from "@/components/site/Breadcrumbs";
import { BUSINESS } from "@/components/site/constants";
import { ResponsivePicture } from "@/components/site/images";
import {
  LECANTO_BLACK_SHINGLE,
  LECANTO_METAL_AFTER,
  LECANTO_METAL_BEFORE,
  LECANTO_STANDING_SEAM_GALVALUME,
} from "@/components/site/project-images";

const PATH = "/roofing-company-lecanto-fl";

const CRUMBS = [
  { label: "Service Areas", href: "/service-areas" },
  { label: "Lecanto, FL", href: PATH },
];

const FAQS: { q: string; a: string }[] = [
  {
    q: "How do I know if my Lecanto roof needs a repair or a full replacement?",
    a: "It depends on the roof's age, overall condition, and the cause of the issue. A newer roof with a clear, isolated cause is usually a good candidate for repair. Replacement typically makes more sense when we see widespread wear — significant granule loss, exposed fiberglass, deteriorating fasteners or sealant, rust on metal panels, or membrane breakdown on flat roofs. We'll walk you through what we find and explain the honest trade-offs during your free inspection.",
  },
  {
    q: "Can you repair a leaking roof in Lecanto, or will I need a full replacement?",
    a: "Yes. Many roof leaks can be repaired without replacing the entire roof, especially when the roof is otherwise in good condition and the source of the leak can be clearly identified. Destination Roofing provides free roof inspections in Lecanto and will recommend repair when it makes sense or replacement when the roof's age or overall condition makes that the better long-term option.",
  },
  {
    q: "Can a new roof help lower my insurance costs in Lecanto?",
    a: "Wind-mitigation improvements made during a roof replacement may help you qualify for insurance credits, though your insurance company determines the actual outcome. We can point out wind-mitigation options during your free inspection.",
  },
  {
    q: "What roofing systems do you install in Lecanto?",
    a: "We install shingle roofing, metal roofing (including standing seam), and flat/low-slope roofing systems, along with gutters and soffit work, for both repairs and full replacements.",
  },
  {
    q: "How quickly can I get a free roof inspection in Lecanto?",
    a: `We aim to respond to inspection requests same day when possible, next day when necessary. Call ${BUSINESS.phone} or submit the form on this page to get started.`,
  },
];

export const Route = createFileRoute("/roofing-company-lecanto-fl")({
  head: () => ({
    meta: [
      { title: "Roofing Company in Lecanto, FL | Destination Roofing" },
      {
        name: "description",
        content:
          "Family-owned, licensed and insured Lecanto roofer for roof repair, replacement, shingles and metal roofing. Free inspections: (352) 901-3094.",
      },
      { name: "robots", content: "index,follow" },
      { property: "og:title", content: "Roofing Company in Lecanto, FL | Destination Roofing" },
      {
        property: "og:description",
        content:
          "Family-owned Lecanto roofer for roof repair, replacement, shingle and metal roofing. Licensed and insured with free roof inspections.",
      },
      { property: "og:url", content: canonical(PATH) },
      { property: "og:type", content: "website" },
      { property: "og:image", content: LECANTO_METAL_AFTER.fallback },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: LECANTO_METAL_AFTER.fallback },
    ],
    links: [{ rel: "canonical", href: canonical(PATH) }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify([
          breadcrumbListJsonLd(CRUMBS),
          {
            "@context": "https://schema.org",
            "@type": "Service",
            name: "Roofing services in Lecanto, FL",
            serviceType: "Roofing Contractor",
            description:
              "Roof repair, roof replacement, shingle roofing, metal roofing and free roof inspections in Lecanto, Florida.",
            url: canonical(PATH),
            provider: { "@id": ROOFING_CONTRACTOR_ID },
            areaServed: { "@type": "City", name: "Lecanto, Florida" },
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQS.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          },
        ]),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <>
      <Breadcrumbs items={CRUMBS} />
      <PageHero
        eyebrow="Local Roofing"
        title="Roofing Company in Lecanto, FL"
        subtitle="Family-owned, licensed and insured roofing for Lecanto homeowners. Roof repair, replacement, shingle and metal roofing with a Free Roof Inspection & Estimate."
      />
      <ServicePageBody title="Your Free Inspection" service="Roofing in Lecanto, FL">
        <p>
          Destination Roofing Inc. serves Lecanto as part of our normal Citrus County service area. We repair and replace shingle, metal and low-slope roofs, and the photographs below show roofing work our crew has actually completed here—not stock examples or projects from another market.
        </p>

        <div className="not-prose bg-muted/50 border border-border rounded-lg p-4 my-6">
          <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
            {[
              "Free Roof Inspection & Estimate",
              "Owner-performed inspections",
              "Family-owned, licensed and insured",
              `License #${BUSINESS.licenseNumber}`,
              "Five-year workmanship warranty on most repairs",
              "15-year workmanship warranty on standard full-roof installations",
            ].map((item) => (
              <li key={item} className="flex items-start gap-2">
                <span className="mt-1 size-1.5 rounded-full bg-cta shrink-0" aria-hidden />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>

        <h2>Roofing Work We’ve Completed in Lecanto</h2>
        <p>
          Real project evidence is more useful than a list of claims. These completed jobs show both replacement work on an aging metal roof and a standing-seam installation. Visit the full <Link to="/projects/lecanto-metal-roof-before-after">Lecanto metal-roof project</Link> for the inspection considerations behind the before-and-after photographs, or browse our <Link to="/gallery">roofing project gallery</Link>.
        </p>
        <div className="not-prose my-6 grid gap-4 md:grid-cols-2">
          <figure className="rounded-xl overflow-hidden border border-border bg-white">
            <ResponsivePicture
              image={LECANTO_METAL_BEFORE}
              alt="Existing deteriorated metal roof before replacement in Lecanto, Florida"
              sizes="(min-width: 768px) 45vw, 100vw"
              imgClassName="w-full h-auto"
            />
            <figcaption className="p-3 text-sm text-muted-foreground"><strong>Before</strong> — existing deteriorated metal roofing in Lecanto.</figcaption>
          </figure>
          <figure className="rounded-xl overflow-hidden border border-border bg-white">
            <ResponsivePicture
              image={LECANTO_METAL_AFTER}
              alt="Completed replacement metal roofing section by Destination Roofing in Lecanto, Florida"
              sizes="(min-width: 768px) 45vw, 100vw"
              imgClassName="w-full h-auto"
            />
            <figcaption className="p-3 text-sm text-muted-foreground"><strong>After</strong> — completed metal roof replacement by Destination Roofing.</figcaption>
          </figure>
        </div>
        <figure className="not-prose my-6 rounded-xl overflow-hidden border border-border bg-white">
          <ResponsivePicture
            image={LECANTO_STANDING_SEAM_GALVALUME}
            alt="26-gauge Galvalume standing seam metal roof installed by Destination Roofing in Lecanto, Florida"
            sizes="(min-width: 1024px) 66vw, 100vw"
            imgClassName="w-full h-auto"
          />
          <figcaption className="p-3 text-sm text-muted-foreground">
            <strong>26-gauge Galvalume standing-seam roof</strong> installed by Destination Roofing in Lecanto, Florida. Its primary fasteners are concealed beneath the seams.{" "}
            <Link to="/projects/standing-seam-metal-roof-lecanto-fl">View the standing-seam project</Link>.
          </figcaption>
        </figure>

        <h2>Repair, Leak Diagnosis or Full Replacement?</h2>
        <p>
          A leak does not automatically mean the whole roof needs to be replaced. The first step is to identify the entry point and inspect the condition around it. An isolated flashing, fastener, sealant or damaged-material problem may support a focused <Link to="/roof-repair">roof repair</Link>. Widespread deterioration, repeated failures or a roof that can no longer hold a reliable repair can make <Link to="/roof-replacement">roof replacement</Link> the more practical option.
        </p>
        <p>
          Destination Roofing evaluates the roofing system rather than relying on one photograph or one visible stain. Most repair work includes a five-year workmanship warranty on the area directly repaired. Standard full-roof installations generally include a 15-year Destination Roofing workmanship warranty. Exact coverage is controlled by the written estimate, contract and warranty.
        </p>

        <h2>Shingle Roofing</h2>
        <p>
          Destination Roofing primarily installs Atlas shingle systems. When evaluating a shingle roof, we look beyond the missing or lifted shingle to the surrounding wear, flashing, penetrations, ventilation and roof deck conditions that can affect a lasting repair or replacement. See our <Link to="/shingle-roofing">shingle roofing guide</Link> for system details and installation practices.
        </p>
        <figure className="not-prose my-6 rounded-xl overflow-hidden border border-border bg-white">
          <ResponsivePicture
            image={LECANTO_BLACK_SHINGLE}
            alt="Completed black architectural shingle roof by Destination Roofing in Lecanto, Florida"
            sizes="(min-width: 1024px) 66vw, 100vw"
            imgClassName="w-full h-auto max-h-[620px] object-cover"
          />
          <figcaption className="p-3 text-sm text-muted-foreground">Completed black architectural shingle roof by Destination Roofing in Lecanto, Florida.</figcaption>
        </figure>

        <h2>Exposed-Fastener and Standing-Seam Metal Roofing</h2>
        <p>
          We install both major residential <Link to="/metal-roofing">metal roofing</Link> systems. Exposed-fastener panels use screws through the panel face and offer a more affordable route to metal roofing, but the exposed screws and washers require inspection as the roof ages. Standing seam conceals its primary fasteners beneath the seams and is the more premium system. The right choice depends on the roof, the planned scope and the homeowner’s priorities—not a one-size-fits-all sales recommendation.
        </p>

        <h2>Wind and Storm-Related Roof Problems</h2>
        <p>
          After severe weather, visible damage may include lifted or missing shingles, displaced flashing, damaged metal panels or leaks around roof penetrations. Some problems are not safely visible from the ground. Our <Link to="/storm-damage-roof-repair">storm-damage roof repair</Link> page explains what to document and when to arrange an inspection. Insurance coverage remains the insurer’s decision; we can document visible roofing conditions and provide a written estimate without promising a claim outcome.
        </p>

        <h2>A Free, Owner-Led Roof Inspection</h2>
        <p>
          Owner and licensed roofing contractor Deston Judy generally performs inspections personally. During a <Link to="/roof-inspection">free roof inspection</Link>, he evaluates the accessible roof conditions, takes photographs, explains the reasonable repair or replacement paths and provides a written estimate. If you want to understand how local homeowners describe working with the company, read our <Link to="/reviews">customer reviews</Link>.
        </p>

        <h2>Frequently Asked Questions</h2>
        <div className="not-prose space-y-3 my-6">
          {FAQS.map((faq) => (
            <details key={faq.q} className="border border-border rounded-lg bg-card">
              <summary className="cursor-pointer font-semibold px-4 py-3">{faq.q}</summary>
              <div className="px-4 pb-4 text-sm text-muted-foreground leading-relaxed">{faq.a}</div>
            </details>
          ))}
        </div>

        <h2>Schedule a Roof Inspection in Lecanto</h2>
        <p>
          If you are deciding between a repair and replacement—or comparing shingle and metal options—start with the roof that is actually on your home. Call <a href={BUSINESS.phoneHref}>{BUSINESS.phone}</a> or <Link to="/contact">request your free roof inspection online</Link>. We will explain what we find and the trade-offs between the reasonable options.
        </p>
      </ServicePageBody>
    </>
  );
}